import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useDataTable } from 'Src/feature/data-table/useDataTable';
import { useEmployee } from 'Src/feature/employee/useEmployee';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { useModalControl } from 'Src/feature/modal/useModalControl';
import { TipAdjustablePayment } from 'Src/feature/payment/paymentService';
import { usePayment } from 'Src/feature/payment/usePayment';
import { useRevenueCenters } from 'Src/feature/revenue-center/useRevenueCenter';
import { useTerminalDefaults } from 'Src/feature/terminal/useTerminalDefaults';
import { SearchComparator as Comp } from 'Src/models/queryOpts';
import useSearchParams from 'Src/routes/useSearchParams';

const searchFilterName = '__CHECKLISTEDITTIPS__search' as never;

const { Provider, Field } = getTypedFormComponents<FilterFormModel>();

class FilterFormModel {
  employeeId: TipAdjustablePayment['employeeId'] | 'all' = 'all';
  revenueCenterId: TipAdjustablePayment['revenueCenterId'] | 'all' = 'all';
  checkStatus: TipAdjustablePayment['checkStatus'] | 'all' = 'all';
  search = '';
}

// convert number value to a string
type TipAdjustForm = Omit<TipAdjustablePayment, 'tipAmount'> & { tipAmount: string };
type UsedTipAdjustForm = ReturnType<typeof useForm<TipAdjustForm>>;

const useCheckListEditTips = () => {
  const { t } = useTranslation();
  const terminalDefaults = useTerminalDefaults();
  const paymentsWithAdjustableTips = usePayment.query.adjustables();
  const clockedInList = useEmployee.query.clockedInList();
  const employeeMap = useEmployee.query.map();
  const revenueCenterGetAll = useRevenueCenters.query.getAll();

  const table = useDataTable({
    defaultPerPage: 100000, // effectively infinity
    data: paymentsWithAdjustableTips.data,
  });

  const { map: searchParams } = useSearchParams();

  const filterForm = useForm<FilterFormModel>({ defaultValues: { ...new FilterFormModel() } });

  const tipAdjustForm = useForm<TipAdjustForm>({
    defaultValues: {
      tipAmount: '0',
    },
  });

  const onAfterChange = (field: keyof FilterFormModel, value: FilterFormModel[keyof FilterFormModel]) => {
    if (field === 'search') {
      console.warn('useCheckListEditTips', 'onAfterChange()', field, 'not supported');
      return;
    }
    const updatedFilter = {
      isDisabled: value === 'all',
      operator: Comp.eq,
      value,
    };

    if (table.store.filter.find(field)) {
      table.store.filter.updateOne(field, updatedFilter);
    } else {
      table.store.filter.findOrCreate(field, updatedFilter);
    }
  };

  const onSearch = () => {
    const searchValue = filterForm.action.getValues().search;
    const compFn = (dataItem: TipAdjustablePayment) => {
      const sv = searchValue.toLowerCase();
      return dataItem.checkNumber.toLowerCase().includes(sv) ||
        dataItem.checkOwner.toLowerCase().includes(sv) ||
        dataItem.checkStatus.toLowerCase().includes(sv) ||
        dataItem.checkTotal.toString().includes(sv) ||
        dataItem.paymentMarker.toLowerCase().includes(sv) ||
        dataItem.paymentAmount.toString().includes(sv) ||
        dataItem.paymentTotal.toString().includes(sv) ||
        dataItem.tipAmount.toString().includes(sv) ||
        dataItem.tableName.toLowerCase().includes(sv);
    };
    if (table.store.filter.find(searchFilterName)) table.store.filter.updateOne(searchFilterName, {
      isDisabled: searchValue.length < 1,
      value: undefined,
      comparator: compFn,
    });
    else table.store.filter.findOrCreate(searchFilterName, {
      isDisabled: searchValue.length < 1,
      value: undefined,
      comparator: compFn,
    });
  };

  const options = {
    status: [
      { value: 'all', label: t('check.labels.allChecks') },
      { value: 'Opened', label: t('labels.open') },
      { value: 'Closed', label: t('labels.closed') },
    ],
    employeeId: [
      { value: 'all', label: t('employee.labels.all') },
      ...(clockedInList.data || []).map((employee) => ({ value: employee.id, label: t('labels.util.employeeFullName', employee) })),
    ],
    revenueCenterId: [
      { value: 'all', label: t('revenueCenter.labels.all') },
      ...(revenueCenterGetAll.data || []).map(({ id, name }) => ({ value: id, label: name })),
    ],
  };

  const editTipModal = useModalControl({
    onClose: () => {
      tipAdjustForm.action.reset(undefined);
    },
  });

  useEffect(() => {
    const searchInputCheckNumber = searchParams.get('checkNumber');
    if (searchInputCheckNumber) {
      filterForm.action.setValue('search', searchInputCheckNumber);
      table.store.filter.findOrCreate(searchFilterName, {
        isDisabled: searchInputCheckNumber.length < 1,
        value: undefined,
        comparator: (dataItem) => dataItem.checkNumber.toLowerCase().includes(searchInputCheckNumber),
      });
    } else if (terminalDefaults.employeeId) {
      filterForm.action.setValue('employeeId', terminalDefaults.employeeId);
      table.store.filter.findOrCreate('employeeId', {
        isDisabled: false,
        value: terminalDefaults.employeeId,
        operator: Comp.eq,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // always start out w/ a fresh grab of the data
    paymentsWithAdjustableTips.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    employeeMap,
    filterForm,
    tipAdjustForm,
    table: {
      ...table,
      isProcessing: paymentsWithAdjustableTips.isLoading,
    },
    onAfterChange,
    onSearch,
    options,
    modal: {
      editTip: editTipModal,
    },
  };
};

export { Field, Provider, useCheckListEditTips };
export type { TipAdjustForm, UsedTipAdjustForm };
